/*###########################################################################
// Walkthrough — first-login onboarding checklist in the header
//#########################################################################*/

function readJson(id) {
  const el = document.getElementById(id)
  if (!el) return null
  try {
    return JSON.parse(el.textContent || '{}')
  } catch {
    return null
  }
}

async function postJson(url, body, csrfName, csrfToken) {
  const payload = new FormData()
  payload.append(csrfName, csrfToken)
  Object.entries(body).forEach(([key, value]) => {
    if (value === undefined || value === null) return
    payload.append(key, String(value))
  })

  const res = await fetch(url, {
    method: 'POST',
    body: payload,
    headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
  })

  return res.json()
}

const AUTO_CLOSE_DELAY = 3200
const POP_DURATION = 900

function createWalkthrough(config) {
  return {
    open: false,
    dismissed: false,
    countBump: false,
    tasks: Array.isArray(config.tasks) ? config.tasks.map((task) => ({ ...task, justCompleted: false })) : [],
    autoCloseTimer: null,

    get doneCount() {
      return this.tasks.filter((task) => task.done).length
    },
    get total() {
      return this.tasks.length
    },
    get allDone() {
      return this.total > 0 && this.doneCount === this.total
    },

    init() {
      window.addEventListener('app:walkthrough-task', (e) => {
        this.completeTask(e.detail?.task)
      })

      // A step can also complete server-side right before a full page
      // reload (e.g. creating a binder redirects to the new binder). The
      // controller flashes which task that was so this fresh page load can
      // still replay the open/animate sequence instead of silently
      // rendering the item as already ticked off.
      if (config.justCompletedTask) {
        this.animateTaskDone(config.justCompletedTask)
      }
    },

    toggle() {
      window.clearTimeout(this.autoCloseTimer)
      this.open = !this.open
    },

    hoverOpen() {
      window.clearTimeout(this.autoCloseTimer)
      this.open = true
    },

    hoverClose() {
      window.clearTimeout(this.autoCloseTimer)
      this.open = false
    },

    // Reveals the dropdown and animates the tick-off so progress is visible
    // immediately. Assumes `task.done` is already true (or is being set true
    // here) — callers decide whether that also needs persisting to the server.
    animateTaskDone(key) {
      const task = this.tasks.find((t) => t.key === key)
      if (!task) return

      task.done = true
      task.justCompleted = true
      this.countBump = true
      this.open = true

      window.clearTimeout(this.autoCloseTimer)
      if (!this.allDone) {
        this.autoCloseTimer = window.setTimeout(() => {
          this.open = false
        }, AUTO_CLOSE_DELAY)
      }

      window.setTimeout(() => {
        task.justCompleted = false
        this.countBump = false
      }, POP_DURATION)
    },

    async completeTask(key) {
      if (!key) return
      const task = this.tasks.find((t) => t.key === key)
      if (!task || task.done) return

      this.animateTaskDone(key)

      try {
        await postJson(config.completeUrl, { task: key }, config.csrfName, config.csrfToken)
      } catch (err) {
        console.warn('Could not save walkthrough progress', err)
      }
    },

    async finish() {
      window.clearTimeout(this.autoCloseTimer)
      this.open = false
      this.dismissed = true
      // Belt-and-suspenders: hide immediately regardless of the x-show
      // binding above so the widget never lingers on screen. $root (not $el)
      // since finish() can be invoked from the nested finish button.
      this.$root.style.display = 'none'

      try {
        await postJson(config.dismissUrl, {}, config.csrfName, config.csrfToken)
      } catch (err) {
        console.warn('Could not dismiss walkthrough', err)
      }
    },
  }
}

// Some pages (e.g. the Pokedex species view) mark a task as just-completed
// via a data attribute on the swapped-in content, because the header —
// which normally replays the animation from a session-flash on full page
// loads — lives outside swup's swap container and doesn't get refreshed on
// SPA navigations. Call this after any content is added to the DOM (initial
// load, swup content:replace) to pick that signal up and forward it through
// the same event the other walkthrough tasks already use.
export function dispatchPendingWalkthroughSignal(root = document) {
  const el = root.querySelector('[data-walkthrough-just-completed]')
  if (!el) return

  const task = el.dataset.walkthroughJustCompleted
  el.remove()
  window.dispatchEvent(new CustomEvent('app:walkthrough-task', { detail: { task } }))
}

export function registerWalkthrough(Alpine) {
  Alpine.data('walkthrough', () => {
    const config = readJson('walkthrough-config')
    if (!config) {
      return {
        open: false,
        dismissed: true,
        countBump: false,
        tasks: [],
        doneCount: 0,
        total: 0,
        allDone: false,
        init() {},
        toggle() {},
        hoverOpen() {},
        hoverClose() {},
        completeTask() {},
        finish() {},
      }
    }

    return createWalkthrough(config)
  })
}
